import type { PrimaryStore } from '../simulation/primary-store';
import type { CometCargo } from '../simulation/comet-recovery';
import type { ClimateTelemetry } from './climate-protocol';

export const PRIMARY_STATE_FLOAT_CHANNELS = 4;

export interface PrimaryTransferViews {
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly mass: Float32Array;
  readonly energy: Float32Array;
  readonly kind: Uint8Array;
  readonly behavior: Uint8Array;
  readonly lineage: Uint32Array;
}

export interface WorkerInitMessage {
  readonly type: 'init';
  readonly seed: number;
  readonly buffers: readonly [ArrayBuffer, ArrayBuffer];
}

export interface WorkerRecycleMessage {
  readonly type: 'recycle';
  readonly buffer: ArrayBuffer;
}

export interface WorkerControlMessage {
  readonly type: 'control';
  readonly paused?: boolean;
  readonly hidden?: boolean;
  readonly restartSeed?: number;
  readonly fireComet?: boolean;
  readonly cometCargo?: CometCargo;
}

export type MainToWorkerMessage =
  | WorkerInitMessage
  | WorkerRecycleMessage
  | WorkerControlMessage;

export interface WorkerReadyMessage {
  readonly type: 'ready';
  readonly seed: number;
  readonly simulationFps: number;
}

export interface WorkerStateMessage {
  readonly type: 'state';
  readonly buffer: ArrayBuffer;
  readonly count: number;
  readonly celestialCount: number;
  readonly step: number;
  readonly simulatedSeconds: number;
  readonly stepDurationMs: number;
  readonly simulationDeltaSeconds: number;
  readonly wallDeltaSeconds: number;
  readonly planetaryAlgaeCoverage: readonly number[];
  readonly planetaryVegetation: Float32Array;
  readonly planetaryClimate: Float32Array;
  readonly climateTelemetry: readonly ClimateTelemetry[];
}

export interface WorkerErrorMessage {
  readonly type: 'worker-error';
  readonly reason: string;
}

export interface WorkerCometStatusMessage {
  readonly type: 'comet-status';
  readonly launched: boolean;
}

export interface WorkerCometImpactMessage {
  readonly type: 'comet-impact';
  readonly x: number;
  readonly y: number;
  readonly strength: number;
  readonly bodyIndex?: number;
  readonly offsetX?: number;
  readonly offsetY?: number;
}

export type WorkerToMainMessage =
  | WorkerReadyMessage
  | WorkerStateMessage
  | WorkerCometStatusMessage
  | WorkerCometImpactMessage
  | WorkerErrorMessage;

export function primaryTransferByteLength(capacity: number): number {
  const floatBytes =
    capacity *
    PRIMARY_STATE_FLOAT_CHANNELS *
    Float32Array.BYTES_PER_ELEMENT;
  const byteChannels = capacity * 2;
  const lineageBytes = capacity * Uint32Array.BYTES_PER_ELEMENT;
  return align4(floatBytes + byteChannels) + lineageBytes;
}

export function createPrimaryTransferBuffer(capacity: number): ArrayBuffer {
  return new ArrayBuffer(primaryTransferByteLength(capacity));
}

export function viewPrimaryTransferBuffer(
  buffer: ArrayBuffer,
  capacity: number,
): PrimaryTransferViews {
  if (buffer.byteLength !== primaryTransferByteLength(capacity)) {
    throw new RangeError('Primary transfer buffer size does not match capacity.');
  }
  const channelBytes = capacity * Float32Array.BYTES_PER_ELEMENT;
  let offset = 0;
  const x = new Float32Array(buffer, offset, capacity);
  offset += channelBytes;
  const y = new Float32Array(buffer, offset, capacity);
  offset += channelBytes;
  const mass = new Float32Array(buffer, offset, capacity);
  offset += channelBytes;
  const energy = new Float32Array(buffer, offset, capacity);
  offset += channelBytes;
  const kind = new Uint8Array(buffer, offset, capacity);
  offset += capacity;
  const behavior = new Uint8Array(buffer, offset, capacity);
  offset = align4(offset + capacity);
  const lineage = new Uint32Array(buffer, offset, capacity);
  return {
    x,
    y,
    mass,
    energy,
    kind,
    behavior,
    lineage,
  };
}

export function writePrimaryTransfer(
  store: PrimaryStore,
  views: PrimaryTransferViews,
): void {
  for (let index = 0; index < store.count; index += 1) {
    views.x[index] = store.x[index] ?? 0;
    views.y[index] = store.y[index] ?? 0;
    views.mass[index] = store.mass[index] ?? 0;
    views.energy[index] = store.energy[index] ?? 0;
    views.kind[index] = store.kind[index] ?? 0;
    views.behavior[index] = store.behavior[index] ?? 0;
    views.lineage[index] = store.lineage[index] ?? 0;
  }
}

function align4(value: number): number {
  return (value + 3) & ~3;
}
